import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { func, string, bool } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { APP_STATE } from '../../../constants'

import ModalWindow from '../ModalWindow'
import { separators } from '../../../constants/formData'
import TooltipIcon from '../TooltipIcon'
import Input from '../Input'
import Dropdown from '../Dropdown'
import DeselectableDropdown from '../DeselectableDropdown'
import useOrganisations from '../system/useOrganisations'
import useBrands from '../system/useBrands'
import { VERMILION, WHITE, TEXT_SMALL } from '../../../constants'
import RuleSelectionSection from '../FeedAuditorComponents/RuleSelectionSection'
import Loader from '../../shared/Loader'

const Grid = styled.div`
  display: grid;
  grid-gap: 18px;
`

const Line = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;
`

// Container for the non-rule checkboxes higher up in the form
const CheckboxesContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 5px;
  padding-bottom: 5px;
`

const ContainerLabel = styled.label`
  width: 100%;
  display: inline-block;
  padding-left: 10px;
`

const UploadButton = styled.div`
  position: absolute;
  height: 55px;
  background: ${VERMILION};
  color: ${WHITE};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: 5px;
`

const StyledInput = styled(Input)`
  position: absolute;
  width: 100%;
  height: 100%;
  cursor: pointer;
`

const ErrorMessage = styled.div`
  position: absolute;
  size: ${TEXT_SMALL};
  color: ${VERMILION};
  bottom: -20px;
  left: 5px;
`

const PartnerErrorMessage = styled.div`
  position: absolute;
  size: ${TEXT_SMALL};
  color: ${VERMILION};
  bottom: -7px;
  left: 5px;
`

const ContainerDiv = styled.div`
  position: relative;
  width: 100%;
`

const AuditRequestDialog = ({
  onCancel,
  onSave,
  onFileUpload,
  visible,
  isAdmin,
  regionList,
  rulesetList,
  rulesList,
  partnersList,
  auditModalRegionsState,
  auditModalRulesState,
  auditModalRulesetsState,
  auditModalPartnersState,
  auditFileState,
}) => {
  const { t } = useTranslation()
  const [auditLabel, setAuditLabel] = useState(null)
  const [fileName, setFileName] = useState('')
  const [delimiter, setDelimiter] = useState(null)
  const [organisation, setOrganisation] = useState(null)
  const [brand, setBrand] = useState(null)
  const [companyIsFMOrg, setCompanyIsFMOrg] = useState(true)
  const [companyName, setCompanyName] = useState(null)
  const [internalAuditFlag, setInternalAuditFlag] = useState(false)
  const [selectedRegion, setSelectedRegion] = useState('')
  const [selectedRuleset, setSelectedRuleset] = useState('')
  const [selectedRulesList, setSelectedRulesList] = useState([])
  const [selectedPartnersList, setSelectedPartnersList] = useState([])
  const [
    collectProductFailureDetails,
    setCollectProductFailureDetails,
  ] = useState(false)

  const orgs = useOrganisations()
  const [formErrors, setFormErrors] = useState({})

  const [data, setData] = useState(null)

  const brands = useBrands()

  useEffect(
    () => {
      // Set the organisation in the form to the one selected in the feed panel by default
      if (visible) {
        setOrganisation(orgs.selected)
      }
    },
    [visible]
  )

  useEffect(
    () => {
      if (auditFileState == APP_STATE.ERROR) {
        toast.error(t('feed_audit_file_upload_failed'))
      } else if (auditFileState == APP_STATE.DONE) {
        // Save to fm-db and send service bus message to fa-processor
        onSave(
          auditLabel,
          selectedRegion,
          delimiter,
          selectedRuleset,
          selectedRulesList,
          selectedPartnersList,
          fileName,
          getCompanyNameForRequest(),
          getBrandNameForRequest(),
          organisation ? organisation.id : null,
          brand ? brand.id : null,
          internalAuditFlag,
          collectProductFailureDetails
        )
        setStateToInitial()
      }
    },
    [auditFileState]
  )

  useEffect(
    () => {
      if (organisation) {
        brands.fetchBrands(organisation.id)
      }
    },
    [organisation]
  )

  useEffect(
    () => {
      if (brands.list.length == 1) {
        setBrand(brands.list[0])
      } else {
        setBrand(null)
      }
    },
    [brands.list]
  )

  useEffect(
    () => {
      setFormErrors({
        noLabel: auditLabel != null && auditLabel.trim().length == 0,
        noRules: selectedRulesList.length === 0,
        noPartners: selectedPartnersList.length === 0,
        noCompanyName: companyIsFMOrg
          ? false
          : companyName != null && companyName.trim().length == 0,
        invalidFileType: fileName ? !isFileTypeValid(fileName) : false,
        invalidFileNameLength: fileName ? fileName.length > 100 : false,
      })
    },
    [
      auditLabel,
      fileName,
      selectedRuleset,
      selectedRulesList,
      selectedPartnersList,
      companyName,
      companyIsFMOrg,
    ]
  )

  const handleFileChange = uploadedFile => {
    //if file is null do nothing
    if (!uploadedFile) {
      return
    }

    setFileName(uploadedFile.name)

    if (!isFileTypeValid(uploadedFile.name)) {
      setFormErrors({ invalidFileType: true })
      return
    }

    if (uploadedFile.name.length > 100) {
      setFormErrors({ invalidFileNameLength: true })
      return
    }

    setData(uploadedFile)
  }

  const getCompanyNameForRequest = () => {
    if (companyIsFMOrg) {
      return organisation.name
    }
    return companyName
  }

  const getBrandNameForRequest = () => {
    if (companyIsFMOrg) {
      return brand.name
    }
    return ''
  }

  const handleSave = () => {
    // Send file data off to fm-api to be uploaded to fa-fileshare
    onFileUpload(data)
    // This is tracked in a useEffect above, if successful will carry on
    // to save to db and send service bus message
  }

  const handleClose = () => {
    onCancel()
    setStateToInitial()
  }

  const setStateToInitial = () => {
    setFileName('')
    setAuditLabel(null)
    setDelimiter(null)
    setSelectedRegion('')
    setData(null)
    setOrganisation(null)
    setBrand(null)
    setInternalAuditFlag(false)
    setCompanyIsFMOrg(true)
    setCompanyName(null)
    setSelectedRuleset('')
    setSelectedRulesList([])
    setSelectedPartnersList([])
  }

  const rulesetChanged = newRuleset => {
    if (newRuleset === '') {
      return
    }
    setSelectedRuleset(newRuleset.id)
    setSelectedRulesList(newRuleset.rules)
  }

  const getFileErrorText = t => {
    if (formErrors.invalidFileType && formErrors.invalidFileNameLength) {
      return (
        t('feed_audit_file_error_type') +
        ' ' +
        t('feed_audit_file_error_length')
      )
    }
    if (formErrors.invalidFileNameLength) {
      return t('feed_audit_file_error_length')
    }
    if (formErrors.invalidFileType) {
      return t('feed_audit_file_error_type')
    }
  }

  function isFormValid() {
    let formIsValid = true

    if (
      auditLabel === '' ||
      auditLabel === null ||
      (auditLabel != null && auditLabel.trim().length == 0) ||
      delimiter === null ||
      selectedRegion === ''
    ) {
      formIsValid = false
    }

    if (companyIsFMOrg) {
      if (organisation === null) {
        formIsValid = false
      }
      if (brand === null) {
        formIsValid = false
      }
    } else {
      if (
        companyName === '' ||
        companyName === null ||
        (companyName != null && companyName.trim().length == 0)
      ) {
        formIsValid = false
      }
    }

    if (
      fileName === '' ||
      fileName === null ||
      fileName.length > 100 ||
      !isFileTypeValid(fileName)
    ) {
      formIsValid = false
    }

    if (selectedRulesList.length === 0 && selectedRuleset === '') {
      formIsValid = false
    }

    if (selectedPartnersList.length === 0) {
      formIsValid = false
    }

    return formIsValid
  }

  function isFileTypeValid(fileNameToCheck) {
    if (fileNameToCheck === '') {
      return false
    }

    if (
      fileNameToCheck.endsWith('.csv') ||
      fileNameToCheck.endsWith('.zip') ||
      fileNameToCheck.endsWith('.gz')
    ) {
      return true
    } else {
      return false
    }
  }

  function isModalDataFetching() {
    //if states are undefined, null, or FETCHING return true
    if (
      !auditModalRegionsState ||
      auditModalRegionsState == APP_STATE.FETCHING ||
      !auditModalPartnersState ||
      auditModalPartnersState == APP_STATE.FETCHING ||
      !auditModalRulesState ||
      auditModalRulesState == APP_STATE.FETCHING ||
      !auditModalRulesetsState ||
      auditModalRulesetsState == APP_STATE.FETCHING ||
      auditFileState == APP_STATE.FETCHING
    ) {
      return true
    }
    return false
  }

  function isModalDataFailed() {
    if (
      auditModalRegionsState == APP_STATE.ERROR ||
      auditModalRulesState == APP_STATE.ERROR ||
      auditModalPartnersState == APP_STATE.ERROR ||
      auditModalRulesetsState == APP_STATE.ERROR
    ) {
      return true
    }
    return false
  }

  function okButtonActive() {
    if (!isFormValid() || isModalDataFetching() || isModalDataFailed()) {
      return true
    }
  }

  return (
    <ModalWindow
      minWidth={'40%'}
      setWidth={'50%'}
      okText={t('new_audit')}
      onCancel={handleClose}
      onOk={handleSave}
      title={t('feed_audit_new_dialog_title')}
      visible={visible}
      okButtonDisabledState={okButtonActive()}
    >
      {isModalDataFetching() ? (
        <Loader />
      ) : isModalDataFailed() ? (
        t('feed_audit_modal_data_failed')
      ) : (
        <Grid>
          <Line>
            <Input
              name="auditLabel"
              type="text"
              label={t('feed_audit_label')}
              onChange={e => setAuditLabel(e.target.value)}
              value={auditLabel}
              setWidth={'-webkit-fill-available'}
            />
            <TooltipIcon
              title={t('feed_audit_label_tooltip')}
              position={'left'}
            />
            {formErrors.noLabel ? (
              <ErrorMessage>{t('feed_audit_label_error')}</ErrorMessage>
            ) : null}
          </Line>
          {isAdmin && (
            <Grid>
              <CheckboxesContainer>
                <TooltipIcon
                  title={t('feed_audit_product_failure_details_tooltip')}
                  position={'right'}
                />
                <ContainerLabel>
                  <input
                    type="checkbox"
                    color={VERMILION}
                    checked={collectProductFailureDetails}
                    style={{ marginRight: 10 }}
                    onChange={event => {
                      setCollectProductFailureDetails(
                        !collectProductFailureDetails
                      )
                    }}
                  />
                  {t('feed_audit_product_failure_details')}
                </ContainerLabel>
                <div />
              </CheckboxesContainer>
              <CheckboxesContainer>
                <TooltipIcon
                  title={t('feed_audit_internal_tooltip')}
                  position={'right'}
                />
                <ContainerLabel>
                  <input
                    type="checkbox"
                    color={VERMILION}
                    checked={internalAuditFlag}
                    style={{ marginRight: 10 }}
                    onChange={event => {
                      setInternalAuditFlag(!internalAuditFlag)
                    }}
                  />
                  {t('feed_audit_internal')}
                </ContainerLabel>
                <ContainerLabel>
                  <input
                    type="checkbox"
                    color={VERMILION}
                    checked={companyIsFMOrg}
                    style={{ marginRight: 10 }}
                    onChange={event => {
                      setOrganisation(null)
                      setBrand(null)
                      setCompanyName(null)
                      setCompanyIsFMOrg(!companyIsFMOrg)
                    }}
                  />
                  {t('feed_audit_is_fm_org')}
                </ContainerLabel>
                <TooltipIcon
                  title={t('feed_audit_is_fm_org_tooltip')}
                  position={'left'}
                />
              </CheckboxesContainer>
              {!companyIsFMOrg && (
                <Line>
                  <Input
                    name="companyName"
                    type="text"
                    label={t('feed_audit_company_name')}
                    onChange={e => setCompanyName(e.target.value)}
                    value={companyName}
                    setWidth={'-webkit-fill-available'}
                  />
                  <TooltipIcon
                    title={t('feed_audit_company_name_tooltip')}
                    position={'left'}
                  />
                  {formErrors.noCompanyName ? (
                    <ErrorMessage>
                      {t('feed_audit_company_name_error')}
                    </ErrorMessage>
                  ) : null}
                </Line>
              )}
            </Grid>
          )}
          <Line>
            <Dropdown
              isHigh
              label={t('organisation')}
              dataId="organisation-switcher"
              options={orgs.list}
              disabled={orgs.list.length <= 1 || !companyIsFMOrg}
              onChange={o => setOrganisation(o)}
              boundValue={organisation}
              placeholder={
                companyIsFMOrg
                  ? t('select')
                  : t('feed_audit_non_fm_client_message')
              }
            />
            <TooltipIcon
              title={
                isAdmin
                  ? t('feed_audit_organisation_tooltip_admin')
                  : t('feed_audit_organisation_tooltip_client')
              }
              position={'left'}
            />
          </Line>
          <Line>
            <Dropdown
              isHigh
              label={t('brand')}
              dataId="brand-switcher"
              options={brands.list}
              disabled={brands.list.length <= 1 || !companyIsFMOrg}
              onChange={b => setBrand(b)}
              boundValue={brand}
              placeholder={
                companyIsFMOrg
                  ? t('select')
                  : t('feed_audit_non_fm_client_message')
              }
            />
            <TooltipIcon
              title={
                isAdmin
                  ? t('feed_audit_brand_tooltip_admin')
                  : t('feed_audit_brand_tooltip_client')
              }
              position={'left'}
            />
          </Line>
          <Line>
            <Input
              name="filename"
              type="text"
              disabled
              label={t('file_name')}
              onChange={e => setFileName(e.target.value)}
              value={fileName}
              setWidth={'-webkit-fill-available'}
            />
            <TooltipIcon
              title={t('feed_audit_file_upload_button_tooltip')}
              position={'left'}
            />
            {formErrors.invalidFileType || formErrors.invalidFileNameLength ? (
              <ErrorMessage>{getFileErrorText(t)}</ErrorMessage>
            ) : null}
          </Line>
          <ContainerDiv>
            <UploadButton>{t('file_upload_button')} </UploadButton>
            <StyledInput
              name="file"
              type="file"
              accept=".csv,.zip,.gz"
              onChange={e => handleFileChange(e.target.files[0])}
              isUpload
            />
          </ContainerDiv>
          <Line>
            <Dropdown
              label={t('file_upload_delimiter')}
              dataId="file-upload-delimiter"
              name="fileUploadDelimiter"
              options={separators}
              boundValue={separators.find(x => x.id === delimiter)}
              placeholder={t('select')}
              onChange={x => setDelimiter(x.id)}
            />
            <TooltipIcon
              title={t('file_upload_dropdown_tooltip')}
              position={'left'}
            />
          </Line>
          <Line>
            <Dropdown
              label={t('feed_audit_region')}
              placeholder={t('select')}
              dataId="audit-region"
              name="auditRegion"
              options={regionList}
              boundValue={regionList.find(r => r.id === selectedRegion)}
              onChange={r => setSelectedRegion(r.id)}
            />
            <TooltipIcon
              title={t('feed_audit_region_tooltip')}
              position={'left'}
            />
          </Line>
          <RuleSelectionSection // Re-use rule selection component for partner selection
            ruleGroupId={'partners'}
            ruleGroupName={'Partner Selection'}
            rulesList={partnersList}
            selectedRulesList={selectedPartnersList}
            setSelectedRulesList={setSelectedPartnersList}
            resetSelectedRuleset={() => {}}
            allowSelectAll={false}
          />
          <Line>
            {formErrors.noPartners ? (
              <PartnerErrorMessage>
                {t('feed_audit_partner_error')}
              </PartnerErrorMessage>
            ) : null}
          </Line>
          <Line>
            <DeselectableDropdown
              label={t('feed_audit_ruleset')}
              placeholder={t('custom')}
              dataId="audit-ruleset"
              name="auditRuleset"
              options={rulesetList}
              boundValue={rulesetList.find(r => r.id === selectedRuleset)}
              onChange={r => rulesetChanged(r)}
            />
            <TooltipIcon
              title={t('feed_audit_ruleset_tooltip')}
              position={'left'}
            />
            {formErrors.noRules ? (
              <ErrorMessage>{t('feed_audit_rules_error')}</ErrorMessage>
            ) : null}
          </Line>
          <span />
          {rulesList.map(group => (
            <RuleSelectionSection
              ruleGroupId={group.id}
              ruleGroupName={group.name}
              rulesList={group.rule}
              selectedRulesList={selectedRulesList}
              setSelectedRulesList={setSelectedRulesList}
              resetSelectedRuleset={() => setSelectedRuleset('')}
            />
          ))}
        </Grid>
      )}
    </ModalWindow>
  )
}

AuditRequestDialog.propTypes = {
  onCancel: func.isRequired,
  onSave: func.isRequired,
  visible: bool.isRequired,
  enableTypeSelection: bool,
  type: string,
  isAdmin: bool,
  regionList: Object.isRequired,
  rulesetList: Object.isRequired,
  rulesList: Object.isRequired,
  partnersList: Object.isRequired,
  auditModalRegionsState: string.isRequired,
  auditModalRulesState: string.isRequired,
  auditModalPartnersState: string.isRequired,
  auditModalRulesetsState: string.isRequired,
}

export default AuditRequestDialog
